import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import ProfilePicture from '@/components/ProfilePicture';
import { Check, X, MessageSquare, UserMinus, Users } from 'lucide-react';

export const ConnectionsSettings = () => {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/users/connections/pending`),
        axios.get(`${API}/users/connections`),
      ]);
      setPending(p.data.pending || []);
      setConnections(c.data.connections || []);
    } catch (e) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (connectionId, action) => {
    setBusyId(connectionId);
    try {
      await axios.post(`${API}/users/connections/respond`, { connection_id: connectionId, action });
      toast.success(action === 'accept' ? 'Connection accepted' : 'Request declined');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (connectionId) => {
    setBusyId(connectionId);
    try {
      await axios.delete(`${API}/users/connections/${connectionId}`);
      toast.success('Connection removed');
      load();
    } catch (e) {
      toast.error('Failed to remove connection');
    } finally {
      setBusyId(null);
    }
  };

  const message = async (userId) => {
    try {
      const r = await axios.post(`${API}/messages/dm/open`, { user_id: userId });
      navigate(`/dashboard/messages?tab=direct&conversation=${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open conversation');
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500 py-4">Loading connections…</p>;
  }

  return (
    <div className="space-y-6" data-testid="connections-content">
      {/* Pending requests */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Pending Requests {pending.length > 0 && <span className="text-cyan-600">({pending.length})</span>}
        </h4>
        {pending.length === 0 ? (
          <p className="text-sm text-gray-400">No pending requests.</p>
        ) : (
          <div className="space-y-2">
            {pending.map((p) => (
              <div key={p.connection_id} className="flex items-center gap-3 p-2 rounded-lg border" data-testid={`pending-${p.id}`}>
                <ProfilePicture src={p.profile_picture} alt={p.full_name} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.full_name}</p>
                  <p className="text-xs text-gray-500 truncate">@{p.username}</p>
                </div>
                <Button size="sm" disabled={busyId === p.connection_id} onClick={() => respond(p.connection_id, 'accept')} data-testid={`accept-${p.id}`} className="bg-gradient-to-r from-cyan-500 to-purple-500">
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" disabled={busyId === p.connection_id} onClick={() => respond(p.connection_id, 'decline')} data-testid={`decline-${p.id}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connected members */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
          <Users className="w-4 h-4" /> My Connections {connections.length > 0 && <span className="text-gray-400">({connections.length})</span>}
        </h4>
        {connections.length === 0 ? (
          <p className="text-sm text-gray-400">You haven't connected with anyone yet. Click a member's avatar in the community to connect.</p>
        ) : (
          <div className="space-y-2">
            {connections.map((c) => (
              <div key={c.connection_id} className="flex items-center gap-3 p-2 rounded-lg border" data-testid={`connection-${c.id}`}>
                <ProfilePicture src={c.profile_picture} alt={c.full_name} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{c.full_name}</p>
                  <p className="text-xs text-gray-500 truncate">@{c.username}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => message(c.id)} data-testid={`message-${c.id}`}>
                  <MessageSquare className="w-4 h-4 mr-1" /> Message
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" disabled={busyId === c.connection_id} onClick={() => remove(c.connection_id)} data-testid={`remove-${c.id}`}>
                  <UserMinus className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionsSettings;
